import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogDescription,
	DialogHeader,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import EEnumPopupPicker from '@/components/everridge/EEnumPopupPicker';
import { OwnerStatus } from '@/features/owners/types/owners-dto';
import { OwnersPageFilterFormSchema } from '@/features/owners/validators/OwnersPageFilterValidator';
import { useOwnersFilter } from '@/features/owners/hooks/useOwnersFilter';
import ApartmentsLookup from '@/features/apartments/components/ApartmentsLookup';
import OwnersLookup from './OwnersLookup';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';

type FormData = z.infer<typeof OwnersPageFilterFormSchema>;

type Props = {
	onClose: () => void;
};

const OwnersPageFilter: FC<Props> = ({ onClose }) => {
	const [isOpen, setIsOpen] = useState(true);

	const { t } = useTranslation();
	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);

	const { filter, setFilter } = useOwnersFilter();

	const { control, handleSubmit, reset } = useForm<FormData>({
		resolver: zodResolver(OwnersPageFilterFormSchema),
		values: {
			apartmentId: filter.apartmentId,
			ownerId: filter.ownerId,
			ownerStatus: filter.ownerStatus,
		},
	});

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	const handleSubmitFilterForm = (form: FormData) => {
		setFilter(form);
		handleClose();
	};

	const handleResetFilter = () => {
		reset({
			apartmentId: [],
			ownerId: [],
			ownerStatus: null,
		});
	};

	const ownerStatuses = Object.values(OwnerStatus).filter(
		(value) => typeof value === 'string',
	);

	return (
		<Dialog
			open={isOpen}
			onOpenChange={handleClose}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t('elements.components.filter.title')}
						<DialogClose
							className={cn(
								'ml-auto flex items-center justify-center cursor-pointer',
								isMobile &&
									'size-11 border border-popover-100 rounded-full',
							)}>
							<Cancel_01_Icon />
						</DialogClose>
					</DialogTitle>
					<DialogDescription />
				</DialogHeader>
				<div className='flex flex-col gap-4'>
					<OwnersLookup
						fieldName={'ownerId'}
						control={control}
						selectVariant={'multi'}
						label={t('elements.form.owners.label')}
						placeholder={t('elements.form.owners.placeholder')}
					/>
					<ApartmentsLookup
						fieldName={'apartmentId'}
						control={control}
						selectVariant={'multi'}
						label={t('elements.form.apartments.label2')}
						placeholder={t('elements.form.apartments.placeholder')}
					/>
					<EEnumPopupPicker
						control={control}
						fieldName={'ownerStatus'}
						list={ownerStatuses}
						label={t('elements.form.statusPicker.label')}
						placeholder={t('main.owners.form.status.placeholder')}
					/>
				</div>
				<DialogFooter
					className={cn(
						'justify-between',
						isMobile && 'mt-auto',
					)}>
					<Button
						variant='outline'
						onClick={handleResetFilter}>
						{t('elements.buttons.reset')}
					</Button>
					<Button onClick={handleSubmit(handleSubmitFilterForm)}>
						{t('elements.buttons.apply')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default OwnersPageFilter;
